import {
    Alert,
    Box,
    Button,
    Card,
    CardMedia,
    Chip,
    Dialog,
    DialogActions,
    FormControl,
    FormHelperText,
    IconButton,
    InputLabel,
    Paper,
    TextField,
    Tooltip,
    Toolbar,
    Typography,
    useMediaQuery,
} from "@mui/material";
import { ContentProps, Toast } from "primereact/toast";
import { useCallback, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { setToastRefToOpen } from "../../../functions/functions";
import { useLocation } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { UserType } from "../../../types/types";
import { userAtom } from "../../../atoms/atoms";
import {
    AccountCircleOutlined,
    CloseOutlined,
    LockPersonOutlined,
    MailLockOutlined,
    PersonOutlineOutlined,
    SaveOutlined,
    VisibilityOffOutlined,
    VisibilityOutlined,
} from "@mui/icons-material";
import validator from "validator";
import updatePasswordByUserId from "../../../resources/Api/authenticator/updatePasswordByUserId";
import updateEmailByUserId from "../../../resources/Api/authenticator/updateEmailByUserId";
import { PROFILE_ACESSES } from "../../../consts/consts";
import { isImpersonationLocked } from "../../../utils/impersonation";

/**
 * ProfilePage - Component that renders a page to manage user profile
 * @component
 */
const ProfilePage = ({ isHiddenMenu }: { isHiddenMenu: boolean }) => {
    const matches = useMediaQuery("(max-width:430px)");

    // atoms
    const getCurrentUser = useRecoilValue<UserType>(userAtom);

    // funcionalities states
    const [isOnline, setIsOnline] = useState<boolean>(true);
    const [isOpenDetails, setIsOpenDetails] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [validationText, setTextValidation] = useState<string>("");
    const [isFieldsValid, setFieldsIsValid] = useState<boolean>(true);
    const [statusMessage, setStatusMessage] = useState<
        "warn" | "success" | "info" | "error" | undefined
    >("warn");
    const [isToastVisible, setIsToastVisible] = useState(false);

    const [email, setEmail] = useState<string>("");

    const [isChangeMail, setIsChangeMail] = useState<boolean>(false);
    const [password, setPassword] = useState<string>("");
    const [secondPassword, setSecondPassword] = useState<string>("");
    const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
    const [isWeakPassword, setIsWeakPassword] = useState(false);

    // refs
    const toastRef = useRef<Toast>(null);

    // cookies
    const [cookies, _, removeCookies] = useCookies([
        "selectedParameters",
        "token",
    ]);

    const location = useLocation();

    useEffect(() => {
        const ref = toastRef;
        if (validationText && !isFieldsValid && !isToastVisible) {
            setToastRefToOpen(
                ref,
                validationText,
                "Atenção!",
                statusMessage,
                undefined,
                3000,
                false
            );
        }
    }, [validationText, isFieldsValid, statusMessage, isToastVisible]);

    const onUpdateEmail = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        setLoading(true);

        const data = {
            id: getCurrentUser.id,
            customerId: getCurrentUser.customer.id,
            email: email,
        };

        try {
            await updateEmailByUserId(token, data);
            setTextValidation("Email atualizado com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);
        } catch (error: any) {
            const { data } = error.response

            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        } finally {
            setTimeout(() => {
                document.location.reload();
            }, 3000);
        }
    }, [cookies.token, cookies.selectedParameters, getCurrentUser.customer.id, getCurrentUser.id, email]);

    const onUpdatePassword = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        setLoading(true);

        const data = {
            id: getCurrentUser.id,
            customerId: getCurrentUser.customer.id,
            password: password,
            isApp: false
        };

        try {
            await updatePasswordByUserId(token, data);
            setTextValidation("Senha alterada com sucesso.");
            setStatusMessage("success");
            setFieldsIsValid(false);
        } catch (error: any) {
            const { data } = error.response

            setLoading(false);
            setFieldsIsValid(false);
            setStatusMessage("warn");
            setTextValidation(data.reason);
        } finally {
            setTimeout(() => {
                removeCookies("token");
                document.location.reload();
            }, 3000);
        }
    }, [cookies.token, cookies.selectedParameters, getCurrentUser.customer.id, getCurrentUser.id, removeCookies, password]);

    const verifyBeforeUpdatePassword = useCallback(() => {
        const ref = toastRef;
        if (!isToastVisible) {
            setToastRefToOpen(
                ref,
                "Ao alterar a senha será necessário fazer o login novamente. Deseja realmente alterar o sua senha?",
                "Atenção!",
                statusMessage,
                {
                    content: (props: ContentProps) => (
                        <Box
                            className="flex flex-column align-items-left"
                            style={{ flex: "1" }}
                        >
                            <Typography>{props?.message.summary}</Typography>
                            <Box className="font-medium text-lg my-3 text-900">
                                {props?.message.detail}
                            </Box>
                            <Button
                                className="p-button-sm flex"
                                style={{ cursor: "pointer", color: "inherit" }}
                                onClick={() => {
                                    ref.current?.clear();
                                    setIsToastVisible(false);
                                    onUpdatePassword();
                                }}
                            >
                                Sim
                            </Button>
                            <Button
                                className="p-button-sm flex"
                                style={{ cursor: "pointer", color: "inherit" }}
                                onClick={() => {
                                    ref.current?.clear();
                                    setIsToastVisible(false);
                                }}
                            >
                                Não
                            </Button>
                        </Box>
                    ),
                },
                undefined,
                true
            );
            setIsToastVisible(true);
        }
    }, [onUpdatePassword, isToastVisible, statusMessage]);

    const verifyBeforeUpdateEmail = useCallback(() => {
        const ref = toastRef;
        if (!isToastVisible) {
            setToastRefToOpen(
                ref,
                "Deseja realmente atualizar o seu e-mail?",
                "Atenção!",
                statusMessage,
                {
                    content: (props: ContentProps) => (
                        <Box
                            className="flex flex-column align-items-left"
                            style={{ flex: "1" }}
                        >
                            <Typography>{props?.message.summary}</Typography>
                            <Box className="font-medium text-lg my-3 text-900">
                                {props?.message.detail}
                            </Box>
                            <Button
                                className="p-button-sm flex"
                                style={{ cursor: "pointer", color: "inherit" }}
                                onClick={() => {
                                    ref.current?.clear();
                                    setIsToastVisible(false);
                                    onUpdateEmail();
                                }}
                            >
                                Sim
                            </Button>
                            <Button
                                className="p-button-sm flex"
                                style={{ cursor: "pointer", color: "inherit" }}
                                onClick={() => {
                                    ref.current?.clear();
                                    setIsToastVisible(false);
                                }}
                            >
                                Não
                            </Button>
                        </Box>
                    ),
                },
                undefined,
                true
            );
            setIsToastVisible(true);
        }
    }, [onUpdateEmail, isToastVisible, statusMessage]);

    const validate = useCallback((value: string) => {
        if (
            validator.isStrongPassword(value, {
                minLength: 8,
                minLowercase: 1,
                minUppercase: 1,
                minNumbers: 1,
                minSymbols: 1,
            })
        ) {
            setIsWeakPassword(false);
        } else {
            setIsWeakPassword(true);
        }
    }, []);

    useEffect(() => {
        if (password) {
            validate(password);
        }
    }, [password, validate]);

    const modalComponent = (
        <>
            <Dialog
                open={isOpenDetails}
                onClose={() => { }}
                hideBackdrop={false}
                style={{ zIndex: 1 }}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    className: "profile-modal-surface tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-shadow-2xl dark:tw-bg-slate-900",
                    sx: { borderRadius: 4, backgroundColor: "#ffff", boxShadow: "0 24px 70px rgba(0, 0, 0, 0.28)" },
                }}
            >
                <Box
                    className="tw-bg-gtr-sidebar tw-px-5 tw-py-4 tw-text-white"
                    sx={{ backgroundImage: "linear-gradient(to right, #006C77, #09414A)", padding: { xs: 2, sm: 2.5 } }}
                >
                    <Typography sx={{ color: "#ffff", fontFamily: "Prometo", fontSize: { xs: 18, sm: 21 }, fontWeight: 700 }}>
                        {isChangeMail ? "Alterar e-mail" : "Alterar senha"}
                    </Typography>
                </Box>
                <Box
                    className="profile-modal-surface container container-fluid tw-bg-white tw-p-4 sm:tw-p-6 dark:tw-bg-slate-900"
                    sx={{
                        padding: { xs: 2, sm: 3 },
                        "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#ffff", transition: "box-shadow 200ms ease, background-color 200ms ease" },
                        "& .MuiOutlinedInput-root:hover": { backgroundColor: "rgba(0, 108, 119, 0.02)" },
                        "& .MuiInputLabel-root.Mui-focused": { color: "#00B700" },
                        "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: "0 0 0 3px rgba(0,183,0,0.12)" },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00B700" },
                    }}
                >
                    {isChangeMail && (
                        <Alert
                            style={{
                                borderRadius: 5,
                                padding: 10,
                                marginBottom: 20,
                            }}
                            severity="info"
                        >
                            {
                                "Insira o seu novo e-mail, para realizar a alteração no cadastro."
                            }
                        </Alert>
                    )}
                    {isChangeMail && (
                        <FormControl
                            className="form-group"
                            style={{ display: "flex", width: "100%" }}
                        >
                            <InputLabel
                                id="demo-multiple-email-label"
                                shrink
                                disabled={false}
                                style={{ fontFamily: "Prometo" }}
                            >
                                E-mail
                            </InputLabel>
                            <TextField
                                name="mail"
                                InputProps={{
                                    label: "E-mail",
                                    notched: true,
                                    style: { fontFamily: "Prometo" },
                                }}
                                disabled={false}
                                value={email}
                                type="text"
                                autoComplete="off"
                                className="form-control form-control-login"
                                placeholder="Digite o seu novo e-mail"
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <IconButton
                                className="visibility-password"
                                disabled
                                edge="end"
                                size="large"
                            >
                                <MailLockOutlined />
                            </IconButton>
                        </FormControl>
                    )}
                    {!isChangeMail && (
                        <>
                            <Alert
                                style={{
                                    borderRadius: 5,
                                    padding: 10,
                                    marginBottom: 20,
                                }}
                                severity="info"
                            >
                                {
                                    "Crie uma nova senha segura, ela deve conter pelo menos uma letra maiúscula, uma letra minúscula, números e um caractere especial. Ex: Gtr789#."
                                }
                            </Alert>
                            <FormControl
                                className="form-group"
                                style={{ display: "flex", width: "100%", marginBottom: 20 }}
                            >
                                <InputLabel
                                    id="demo-multiple-password-label"
                                    shrink
                                    style={{ fontFamily: "Prometo" }}
                                    disabled={false}
                                >
                                    Senha
                                </InputLabel>
                                <TextField
                                    name="password"
                                    autoComplete="off"
                                    InputProps={{
                                        label: "Senha",
                                        notched: true,
                                        style: { fontFamily: "Prometo" },
                                    }}
                                    disabled={false}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                    }}
                                    type={isPasswordVisible ? "text" : "password"}
                                    className="form-control form-control-login"
                                    placeholder="Digite a senha"
                                />
                                <IconButton
                                    aria-label="toggle password visibility"
                                    className="visibility-password"
                                    onClick={() => {
                                        setIsPasswordVisible(!isPasswordVisible);
                                    }}
                                    edge="end"
                                    size="large"
                                    disabled={false}
                                >
                                    {isPasswordVisible ? (
                                        <VisibilityOffOutlined />
                                    ) : (
                                        <VisibilityOutlined />
                                    )}
                                </IconButton>
                            </FormControl>

                            <FormControl
                                className="form-group"
                                style={{ display: "flex", width: "100%" }}
                            >
                                <TextField
                                    name="password"
                                    autoComplete="off"
                                    InputProps={{
                                        notched: true,
                                        style: { fontFamily: "Prometo" },
                                    }}
                                    disabled={false}
                                    value={secondPassword}
                                    onChange={(e) => {
                                        setSecondPassword(e.target.value);
                                    }}
                                    type={isPasswordVisible ? "text" : "password"}
                                    className="form-control form-control-login"
                                    placeholder="Confirme a senha"
                                />

                                <Box style={{ display: "flex", marginTop: 10, marginLeft: -15 }}>
                                    <FormHelperText
                                        style={{
                                            color: isWeakPassword ? "red" : "green",
                                            display: "flex",
                                        }}
                                    >
                                        {isWeakPassword &&
                                            password &&
                                            "A senha deve conter pelo menos uma letra maiúscula, uma letra minúscula, números e um caractere especial."}
                                        {!isWeakPassword && password && (
                                            <>
                                                <LockPersonOutlined style={{ marginRight: 5 }} />
                                                <Typography>Senha forte</Typography>
                                            </>
                                        )}
                                    </FormHelperText>
                                </Box>
                            </FormControl>
                        </>
                    )}
                </Box>
                <DialogActions sx={{ borderTop: "1px solid rgba(0, 0, 0, 0.10)", padding: 2 }}>
                    <Box
                        className="tw-flex tw-w-full tw-justify-end"
                        sx={{ display: "flex", width: "100%", justifyContent: "flex-end" }}
                    >
                        <Paper
                            className="tw-flex tw-w-full tw-flex-col-reverse tw-gap-2 sm:tw-w-auto sm:tw-flex-row"
                            elevation={0}
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column-reverse", sm: "row" },
                                justifyContent: "flex-end",
                                width: { xs: "100%", sm: "auto" },
                                marginLeft: "auto",
                                alignSelf: "flex-end",
                                gap: 1,
                                background: "transparent",
                            }}
                        >
                            <Button
                                type="button"
                                style={{
                                    fontFamily: "Prometo",
                                    color: "#ffff",
                                    marginRight: 5,
                                }}
                                disabled={false}
                                onClick={() => {
                                    if (!isToastVisible) {
                                        setIsOpenDetails(false);
                                        setIsChangeMail(false);
                                        setIsPasswordVisible(false);
                                        setIsToastVisible(false);
                                        setEmail("");
                                        setPassword("");
                                        setSecondPassword("");
                                    }
                                }}
                                className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <CloseOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Cancelar
                                </Typography>
                            </Button>
                            <Button
                                type="button"
                                style={{ fontFamily: "Prometo", color: "#ffff" }}
                                disabled={false}
                                onClick={() => {
                                    if (isChangeMail) {
                                        if (!email) {
                                            setTextValidation("O campo e-mail não pode ser vazio.");
                                            setStatusMessage("warn");
                                            setFieldsIsValid(false);
                                            setLoading(false);
                                            return;
                                        } else {
                                            verifyBeforeUpdateEmail();
                                        }
                                    } else {
                                        if (!password && !secondPassword) {
                                            setTextValidation("As senhas não podem ser vazias.");
                                            setStatusMessage("warn");
                                            setFieldsIsValid(false);
                                            setLoading(false);
                                            return;
                                        }
                                        if (password !== secondPassword) {
                                            setTextValidation("As senhas devem ser iguais.");
                                            setStatusMessage("warn");
                                            setFieldsIsValid(false);
                                            setLoading(false);
                                            return;
                                        } else {
                                            verifyBeforeUpdatePassword();
                                        }
                                    }
                                }}
                                className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                            >
                                <SaveOutlined style={{ marginRight: 5 }} />
                                <Typography
                                    className="forgot-password"
                                    style={{
                                        fontFamily: "Prometo",
                                        fontSize: "16px",
                                        color: "#ffff",
                                    }}
                                >
                                    Salvar
                                </Typography>
                            </Button>
                        </Paper>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );

    return (
        <Box
            className="profile-root grid-box-out-container tw-flex tw-min-h-screen tw-w-full tw-items-center tw-justify-center tw-bg-transparent tw-p-3 sm:tw-p-5"
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                minHeight: "100vh",
                padding: 2,
                background: "transparent",
            }}
        >
            <Toast
                ref={toastRef}
                position="center"
                style={{ zIndex: 99999 }}
                onShow={() => {
                    setIsToastVisible(true);

                    if (validationText) {
                        setTimeout(() => {
                            setFieldsIsValid(true);
                            setIsToastVisible(false);
                        }, 3000);
                    }
                }}
            />
            <Box
                className="profile-main-surface grid-container container container-fluid tw-w-full tw-overflow-hidden tw-rounded-2xl tw-border-0 tw-bg-white tw-p-2 tw-shadow-none sm:tw-p-3 dark:tw-bg-slate-900"
                style={{
                    marginTop: 0,
                    padding: 10,
                    background: "#ffff",
                    transition: "all 0.5s ease-out allow-discrete",
                    width: matches ? "100%" : isHiddenMenu ? "min(92vw, 980px)" : "min(86vw, 980px)",
                    borderRadius: 16,
                    // height: 650
                }}
            >
                <Toolbar
                    disableGutters
                    className="grid-header-bar tw-mb-3 tw-min-h-[64px] tw-rounded-xl tw-bg-gtr-sidebar tw-px-3 tw-text-white tw-shadow-sm sm:tw-px-4"
                    sx={{ backgroundImage: "linear-gradient(to right, #006C77, #09414A)", fontFamily: "Prometo", color: "#fff" }}
                >
                    <Box className="header-actions tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3">
                        <Box className="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
                            <Box className="tw-flex tw-h-10 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-rounded-xl tw-bg-white/10">
                                <AccountCircleOutlined />
                            </Box>
                            <Typography className="tw-truncate" sx={{ fontFamily: "Prometo", fontSize: { xs: 17, sm: 19 }, fontWeight: 700 }}>
                                Meu perfil
                            </Typography>
                        </Box>
                        <Box className="tw-flex tw-flex-none tw-items-center tw-gap-1.5">
                            <Chip
                                size="small"
                                sx={{ backgroundColor: "#00B700", color: "#fff", fontFamily: "Prometo", fontWeight: 700 }}
                                clickable={false}
                                onClick={() => { }}
                                label={getCurrentUser.configs.isBlocked ? "Bloqueado" : "Ativo"}
                            />
                            <Chip
                                size="small"
                                className="tw-hidden sm:tw-flex"
                                sx={{ backgroundColor: "#00B700", color: "#fff", fontFamily: "Prometo", fontWeight: 700 }}
                                clickable={false}
                                onClick={() => { }}
                                label={getCurrentUser.accessProfile.name}
                            />
                            {getCurrentUser.accessProfile.id !== Number(PROFILE_ACESSES.DRIVER) && (
                                <Tooltip title="Fechar" arrow>
                                    <IconButton
                                        aria-label="Fechar perfil"
                                        onClick={() => window.location.replace("/")}
                                        sx={{ color: "#fff", borderRadius: 2, "&:hover": { backgroundColor: "rgba(255,255,255,0.12)" } }}
                                    >
                                        <CloseOutlined />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Toolbar>

                <Box
                    className="profile-content-surface"
                    style={{
                        marginTop: 2,
                        borderRadius: 5,
                        backgroundColor: "#ffff",
                        fontFamily: "Prometo",
                    }}
                >
                    {!loading ? (
                        <>
                            <Box
                                className="profile-layout-surface tw-grid tw-grid-cols-1 tw-gap-4 tw-rounded-xl tw-border-0 tw-bg-slate-50 tw-p-3 md:tw-grid-cols-[240px_minmax(0,1fr)] sm:tw-p-4 dark:tw-bg-slate-950"
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "260px minmax(0, 1fr)" },
                                    gap: 2,
                                    padding: { xs: 2, sm: 2.5 },
                                    backgroundColor: "#f8fafc",
                                    margin: 0,
                                    borderRadius: 12,
                                    marginBottom: 16,
                                    "& .MuiOutlinedInput-root": { borderRadius: 2, backgroundColor: "#fff" },
                                }}
                            >
                                {/*content*/}
                                {modalComponent}
                                <Box
                                    className="tw-min-w-0"
                                    style={{ margin: 0 }}
                                >
                                    <Toolbar
                                        disableGutters
                                        className="tw-flex tw-h-full tw-min-h-[250px] tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-bg-gtr-sidebar tw-p-5 tw-text-white tw-shadow-md"
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            backgroundImage:
                                                "linear-gradient(to right, #006C77 , #09414A)",
                                            borderRadius: 12,
                                        }}
                                    >
                                        <Box
                                            className="header-image"
                                            sx={{
                                                borderRadius: "100px",
                                                justifySelf: "center",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                height: "112px",
                                                width: 112,
                                                background: "rgba(255,255,255,0.12)",
                                                margin: 1,
                                                backgroundImage: `url(${require("../../../assets/files/maps-image.png")})`,
                                                border: "2px solid rgba(255,255,255,0.28)",
                                                boxShadow: "0 10px 24px rgba(0,0,0,0.18)",
                                            }}
                                        >
                                            {!getCurrentUser?.configs.isManager && !getCurrentUser.customer.image ? (
                                                <Card
                                                    style={{
                                                        height: 88,
                                                        width: 88,
                                                        borderRadius: 100,
                                                        margin: 0,
                                                        background: "rgba(255,255,255,0.94)",
                                                    }}
                                                >
                                                    <AccountCircleOutlined
                                                        style={{ height: 88, width: 88, fill: "#dbdbdb" }}
                                                    />
                                                </Card>
                                            ) : (
                                                <CardMedia
                                                    style={{
                                                        height: 88,
                                                        width: 88,
                                                        borderRadius: 100,
                                                        margin: 0,
                                                        objectFit: "contain",
                                                    }}
                                                    component="img"
                                                    alt="Imagem"
                                                    image={!getCurrentUser?.configs.isManager ? getCurrentUser?.customer.image : require("../../../assets/files/tracker.png")}
                                                />
                                            )}
                                        </Box>
                                        <Box
                                            className="header-title"
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                fontFamily: "Prometo",
                                            }}
                                        >
                                            <h6
                                                className="element-on-header tw-m-0 tw-break-words tw-text-center tw-text-sm tw-font-bold tw-text-white"
                                                style={{
                                                    lineBreak: "anywhere",
                                                    padding: 10,
                                                    marginBottom: 0,
                                                    color: "#ffff",
                                                    alignSelf: "center",
                                                    justifySelf: "center",
                                                    fontSize: "14px"
                                                }}
                                            >
                                                {getCurrentUser.name}
                                            </h6>
                                        </Box>
                                        <Typography
                                            sx={{
                                                maxWidth: "100%",
                                                color: "rgba(255,255,255,0.72)",
                                                fontFamily: "Prometo",
                                                fontSize: 12,
                                                textAlign: "center",
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            {getCurrentUser?.customer?.reducedName}
                                        </Typography>
                                        <Box
                                            className="header-actions"
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                alignContent: "center",
                                                fontFamily: "Prometo",
                                            }}
                                        ></Box>
                                    </Toolbar>
                                </Box>
                                <Box className="profile-account-surface tw-flex tw-min-w-0 tw-flex-col tw-justify-center tw-gap-4 tw-rounded-xl tw-border-0 tw-bg-white tw-p-4 tw-shadow-none sm:tw-p-5 dark:tw-bg-slate-900">
                                <Box>
                                    <Typography sx={{ color: "#0f172a", fontFamily: "Prometo", fontSize: 17, fontWeight: 700 }}>
                                        Dados da conta
                                    </Typography>
                                    <Typography sx={{ mt: 0.4, color: "#64748b", fontFamily: "Prometo", fontSize: 12.5 }}>
                                        Informações vinculadas ao seu acesso no sistema.
                                    </Typography>
                                </Box>
                                <Box
                                    className="tw-w-full"
                                    style={{ margin: 0 }}
                                >
                                    <FormControl
                                        className="form-group"
                                        style={{ display: "flex", width: "100%" }}
                                    >
                                        <InputLabel
                                            id="demo-multiple-name-label"
                                            shrink
                                            disabled={true}
                                            style={{ fontFamily: "Prometo" }}
                                        >
                                            Nome
                                        </InputLabel>
                                        <TextField
                                            name="name"
                                            InputProps={{
                                                label: "Nome",
                                                notched: true,
                                                style: { fontFamily: "Prometo" },
                                            }}
                                            disabled={true}
                                            value={getCurrentUser.name}
                                            type="text"
                                            autoComplete="off"
                                            className="form-control form-control-login"
                                            placeholder=""
                                        />
                                        <IconButton
                                            className="visibility-password"
                                            disabled
                                            edge="end"
                                            size="large"
                                        >
                                            <PersonOutlineOutlined />
                                        </IconButton>
                                    </FormControl>
                                </Box>
                                {getCurrentUser.accessProfile.id !== Number(PROFILE_ACESSES.DRIVER) && <Box
                                    className="tw-w-full"
                                    style={{ margin: 0 }}
                                >
                                    <FormControl
                                        className="form-group"
                                        style={{ display: "flex", width: "100%" }}
                                    >
                                        <InputLabel
                                            id="demo-multiple-email-label"
                                            shrink
                                            disabled={true}
                                            style={{ fontFamily: "Prometo" }}
                                        >
                                            E-mail
                                        </InputLabel>
                                        <TextField
                                            name="mail"
                                            InputProps={{
                                                label: "E-mail",
                                                notched: true,
                                                style: { fontFamily: "Prometo" },
                                            }}
                                            disabled={true}
                                            value={getCurrentUser.email}
                                            type="text"
                                            autoComplete="off"
                                            className="form-control form-control-login"
                                            placeholder=""
                                        />
                                        <IconButton
                                            className="visibility-password"
                                            disabled
                                            edge="end"
                                            size="large"
                                        >
                                            <MailLockOutlined />
                                        </IconButton>
                                    </FormControl>
                                </Box>}
                                </Box>
                            </Box>
                        </>
                    ) : (
                        <div
                            style={{
                                width: "100%",
                                padding: 20,
                                display: "flex",
                                alignItems: "center",
                                flexDirection: "column",
                            }}
                        >
                            <div className="tracker-loading">
                                <div className="ld-tracker">
                                    <div></div>
                                    <div></div>
                                    <div></div>
                                </div>
                            </div>
                        </div>
                    )}
                </Box>
                <Box className="tw-mt-3 tw-flex tw-flex-wrap tw-justify-end tw-gap-2 tw-border-t tw-border-slate-200 tw-pt-3 dark:tw-border-slate-700">
                    {getCurrentUser.accessProfile.id !== Number(PROFILE_ACESSES.DRIVER) && <Box>
                        <Typography className="forgot-password">
                            <Button
                                disabled={isImpersonationLocked()}
                                className="forgot-password-button !tw-min-h-10 !tw-rounded-lg !tw-border !tw-border-gtr-green/30 !tw-px-4 !tw-font-prometo !tw-font-bold !tw-text-gtr-green tw-transition-all tw-duration-200 hover:!tw-bg-green-50"
                                size="small"
                                startIcon={<MailLockOutlined />}
                                onClick={(e) => {
                                    setIsChangeMail(true);
                                    setIsOpenDetails(true);
                                }}
                            >
                                Alterar e-mail
                            </Button>
                        </Typography>
                    </Box>}
                    {!getCurrentUser.configs.useGtf && getCurrentUser.accessProfile.id !== Number(PROFILE_ACESSES.DRIVER) && <Box>
                        <Typography className="forgot-password">
                            <Button
                                className="forgot-password-button !tw-min-h-10 !tw-rounded-lg !tw-border !tw-border-gtr-green/30 !tw-px-4 !tw-font-prometo !tw-font-bold !tw-text-gtr-green tw-transition-all tw-duration-200 hover:!tw-bg-green-50"
                                size="small"
                                startIcon={<LockPersonOutlined />}
                                onClick={(e) => {
                                    setIsChangeMail(false);
                                    setIsOpenDetails(true);
                                }}
                            >
                                Alterar senha
                            </Button>
                        </Typography>
                    </Box>}
                </Box>
            </Box>
        </Box>
    );
};

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "#f3f4f6",
    border: "none",
    boxShadow: "none",
    borderRadius: 1,
    color: "black",
    justifyContent: "center",
    alignItems: "center",
    lineBreak: "anywhere",
    padding: 3,
    fontFamily: "Prometo",
    maxWidth: 900,
};

export default ProfilePage;
